// Reporting date presets → explicit {from, to} ranges — PURE.
//
// Computed client-side (the spec's "computed our side") so the server stays a
// pure `WHERE date BETWEEN @from AND @to` query. Ranges are inclusive on both
// ends and use the LOCAL calendar of the passed `now` (the browser's day), so
// "Today" means the user's today. `now` is injected (no clock read) for testable,
// deterministic ranges.
//
// ⚠️⚠️ THIS FILE IS ONE END OF A TWO-ENDED INVARIANT. The other is
// `server/modules/reporting/pullWindow.ts`, which decides which dates the pull job
// asks Meta for:
//
//   The pull window must ALWAYS contain the local calendar date that any viewer
//   could be calling "today".
//
// ⚠️ THE LOCAL CALENDAR HERE IS DELIBERATE AND MUST NOT BE "FIXED" TO UTC. Until
// 2026-08-27 the pull computed its window off the UTC date while this file
// resolved the browser's; Brisbane is UTC+10, so the two agreed for only fourteen
// hours a day and every morning "Today" was empty AND month-to-date spend was
// silently understated (this_week / this_month include today — see below). The
// pull is the side that was wrong about which dates it needed, and it is the side
// that was changed. If a preset here ever stops ending at the local today, revisit
// that padding rather than moving the AM's day off their own calendar.

import type { ReportingDatePreset } from '../types/reporting.js';

export interface DateRange {
  from: string; // YYYY-MM-DD (inclusive)
  to: string; // YYYY-MM-DD (inclusive)
}

/**
 * A Date → YYYY-MM-DD in the LOCAL calendar.
 *
 * ⚠️⚠️ EXPORTED, and never to be replaced with `toISOString().slice(0, 10)`.
 * That reads the UTC date, and Brisbane is UTC+10 all year — so before 10:00
 * local it names YESTERDAY. That is exactly the off-by-one-day fault the pull
 * window carried until 2026-08-27 (see this file's header); anything asking
 * "what is today for this viewer" should come through here.
 */
export function toLocalYmd(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

// Most recent Monday on/before `d` (ISO week, Monday start).
function startOfWeekMonday(d: Date): Date {
  const dow = (d.getDay() + 6) % 7; // Mon=0 … Sun=6
  return addDays(d, -dow);
}

// Resolve a preset to a concrete range. `custom` is required for the 'custom'
// preset (the caller supplies the picked range). Rolling windows are N COMPLETE
// days ending YESTERDAY — they EXCLUDE today's partial (incomplete) day (e.g.
// Past 7 Days = today-7 … today-1). `this_week` / `this_month` still include
// today by convention (week-to-date / month-to-date).
export function presetToRange(
  preset: ReportingDatePreset,
  now: Date,
  custom?: DateRange,
): DateRange {
  const today = toLocalYmd(now);
  switch (preset) {
    case 'today':
      return { from: today, to: today };
    case 'yesterday': {
      const y = toLocalYmd(addDays(now, -1));
      return { from: y, to: y };
    }
    case 'this_week':
      return { from: toLocalYmd(startOfWeekMonday(now)), to: today };
    case 'this_month':
      return { from: toLocalYmd(new Date(now.getFullYear(), now.getMonth(), 1)), to: today };
    // The whole previous calendar month — a COMPLETE window, unlike the
    // month-to-date presets around it. ⚠️ Deliberately the same month math as
    // the Business lens's `last_month` (monthStart / prevMonthEnd below, hoisted
    // declarations) rather than a second implementation: two surfaces disagreeing
    // about what "last month" means would be invisible until someone compared
    // two reports.
    case 'last_month':
      return { from: monthStart(now, 1), to: prevMonthEnd(now) };
    case 'this_quarter': {
      // Start of the current calendar quarter (Jan/Apr/Jul/Oct 1) → today.
      const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
      return { from: toLocalYmd(new Date(now.getFullYear(), quarterStartMonth, 1)), to: today };
    }
    case 'this_year':
      return { from: toLocalYmd(new Date(now.getFullYear(), 0, 1)), to: today };
    // Rolling windows ALL end YESTERDAY (exclude today's incomplete day): N
    // complete days from today-N through today-1 inclusive. Every "Past N Days"
    // preset uses this identical convention (only N differs).
    case 'past_7':
      return { from: toLocalYmd(addDays(now, -7)), to: toLocalYmd(addDays(now, -1)) };
    case 'past_14':
      return { from: toLocalYmd(addDays(now, -14)), to: toLocalYmd(addDays(now, -1)) };
    case 'past_30':
      return { from: toLocalYmd(addDays(now, -30)), to: toLocalYmd(addDays(now, -1)) };
    case 'past_60':
      return { from: toLocalYmd(addDays(now, -60)), to: toLocalYmd(addDays(now, -1)) };
    case 'past_90':
      return { from: toLocalYmd(addDays(now, -90)), to: toLocalYmd(addDays(now, -1)) };
    case 'past_180':
      return { from: toLocalYmd(addDays(now, -180)), to: toLocalYmd(addDays(now, -1)) };
    case 'past_365':
      return { from: toLocalYmd(addDays(now, -365)), to: toLocalYmd(addDays(now, -1)) };
    case 'custom':
      if (!custom) throw new Error('custom preset requires an explicit range');
      return { from: custom.from, to: custom.to };
  }
}

// The immediately-preceding, equal-length window (for "vs previous period"). For
// [from, to] of length N days, returns the N days ending the day before `from`.
export function previousRange(range: DateRange): DateRange {
  const from = new Date(`${range.from}T00:00:00Z`);
  const to = new Date(`${range.to}T00:00:00Z`);
  const lengthDays = Math.round((to.getTime() - from.getTime()) / 86_400_000) + 1;
  const prevTo = new Date(from.getTime() - 86_400_000); // day before `from`
  const prevFrom = new Date(prevTo.getTime() - (lengthDays - 1) * 86_400_000);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(prevFrom), to: iso(prevTo) };
}

// ── Business-lens (Account Analytics) MONTHLY presets ───────────────────────
// The financial/performance lens works at MONTHLY grain over a much wider history
// than the daily reporting presets (Xero back to 2023), so it has its own preset
// set. Ranges are whole-calendar-month bounded; "All Time" starts at the earliest
// data month (data-dependent → the caller supplies it). The month-domain is later
// enumerated from these {from, to} via enumerateBucketDomain(...,'monthly').
export type BusinessLensDatePreset =
  | 'this_month'
  | 'last_month'
  | 'past_3m'
  | 'past_6m'
  | 'past_12m'
  | 'all_time';

export const BUSINESS_LENS_PRESETS: { value: BusinessLensDatePreset; label: string }[] = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'past_3m', label: 'Past 3M' },
  { value: 'past_6m', label: 'Past 6M' },
  { value: 'past_12m', label: 'Past 12M' },
  { value: 'all_time', label: 'All Time' },
];

export const DEFAULT_BUSINESS_LENS_PRESET: BusinessLensDatePreset = 'past_12m';

// First day of the month `offset` months before `now`'s month (offset 0 = current
// month). JS Date normalises a negative/overflowing month across year boundaries.
function monthStart(now: Date, offset: number): string {
  return toLocalYmd(new Date(now.getFullYear(), now.getMonth() - offset, 1));
}

// Last day of the month BEFORE `now`'s month (day 0 = the prior month's last day).
function prevMonthEnd(now: Date): string {
  return toLocalYmd(new Date(now.getFullYear(), now.getMonth(), 0));
}

// Resolve a business-lens preset to a concrete {from, to}. This Month is the
// current month-to-date; Last Month is the whole previous calendar month. Trailing
// "Past N months" is the N FULLY-COMPLETED months ending LAST month — it EXCLUDES
// the current in-progress month (e.g. now=2026-07 → Past 12M = 2025-07-01 …
// 2026-06-30). "All Time" runs from `earliestMonth` ('YYYY-MM') to today, falling
// back to Past 12M when there's no data. `now` is injected (no clock read) for
// deterministic ranges.
export function businessLensRange(
  preset: BusinessLensDatePreset,
  now: Date,
  earliestMonth?: string | null,
): DateRange {
  const today = toLocalYmd(now);
  switch (preset) {
    case 'this_month':
      return { from: monthStart(now, 0), to: today };
    case 'last_month':
      return { from: monthStart(now, 1), to: prevMonthEnd(now) };
    // Trailing windows end at the close of LAST month (current month excluded);
    // `from` = start of the month N before the current one, so exactly N complete
    // months are covered (Past 3M = the month 3 back … the month 1 back).
    case 'past_3m':
      return { from: monthStart(now, 3), to: prevMonthEnd(now) };
    case 'past_6m':
      return { from: monthStart(now, 6), to: prevMonthEnd(now) };
    case 'past_12m':
      return { from: monthStart(now, 12), to: prevMonthEnd(now) };
    case 'all_time':
      return earliestMonth
        ? { from: `${earliestMonth}-01`, to: today }
        : { from: monthStart(now, 12), to: prevMonthEnd(now) };
  }
}
